import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  DialogTitle,
  IconButton,
  LinearProgress,
  Modal,
  ModalClose,
  ModalDialog,
  Sheet,
  Typography,
} from '@mui/joy';
import { DownloadSimple, File as FileIcon, Paperclip, Trash, X } from '@phosphor-icons/react';
import { downloadApiBlob, fetchApiBlob } from '../../../lib/api';
import type { DevTicketAttachment } from '../../../types/admin';
import { TicketError } from './ticketShared';
import { ACCEPT, addDraftFiles } from './ticketFiles';

const bytes = (size: number) =>
  size < 1024 * 1024
    ? `${Math.max(1, Math.round(size / 1024))} KB`
    : `${(size / (1024 * 1024)).toFixed(1)} MB`;
function DraftThumbnail({ file }: { file: File }) {
  const ref = useRef<HTMLImageElement>(null);
  useEffect(() => {
    if (!file.type.startsWith('image/') || !ref.current) return;
    const next = URL.createObjectURL(file);
    ref.current.src = next;
    return () => URL.revokeObjectURL(next);
  }, [file]);
  return file.type.startsWith('image/') ? (
    <Box
      component="img"
      ref={ref}
      alt=""
      sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 'xs' }}
    />
  ) : (
    <FileIcon size={24} />
  );
}
export function FilePicker({
  files,
  onChange,
  disabled,
  label = 'Attach files',
}: {
  files: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
  label?: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<unknown>(null);
  const add = (incoming: File[]) => {
    if (disabled) return;
    try {
      onChange(addDraftFiles(files, incoming));
      setError(null);
    } catch (problem) {
      setError(problem);
    }
  };
  return (
    <Box
      onDragOver={(event) => {
        if (event.dataTransfer.types.includes('Files')) event.preventDefault();
      }}
      onDrop={(event) => {
        if (event.dataTransfer.files.length) {
          event.preventDefault();
          event.stopPropagation();
          add(Array.from(event.dataTransfer.files));
        }
      }}
      sx={{ minWidth: 0 }}
    >
      <input
        ref={input}
        aria-label={label}
        type="file"
        multiple
        accept={ACCEPT}
        hidden
        disabled={disabled}
        onChange={(event) => {
          add(Array.from(event.target.files ?? []));
          event.target.value = '';
        }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Button
          size="sm"
          variant="plain"
          color="neutral"
          startDecorator={<Paperclip size={16} />}
          disabled={disabled}
          onClick={() => input.current?.click()}
        >
          {label}
        </Button>
        <Typography level="body-xs">Paste a screenshot or drop files here · 20 MB per file</Typography>
      </Box>
      <TicketError error={error} />
      {files.length > 0 && (
        <Box sx={{ display: 'grid', gap: 0.75, my: 1 }}>
          {files.map((file, index) => (
            <Sheet
              variant="soft"
              key={`${file.name}-${index}`}
              sx={{ p: 1, borderRadius: 'sm', display: 'flex', gap: 1, alignItems: 'center', minWidth: 0 }}
            >
              <DraftThumbnail file={file} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography level="body-sm" sx={{ overflowWrap: 'anywhere' }}>
                  {file.name}
                </Typography>
                <Typography level="body-xs">{bytes(file.size)} · Ready to upload</Typography>
              </Box>
              <IconButton
                size="sm"
                aria-label={`Remove ${file.name} from draft`}
                disabled={disabled}
                onClick={() => onChange(files.filter((_, i) => i !== index))}
              >
                <X />
              </IconButton>
            </Sheet>
          ))}
        </Box>
      )}
    </Box>
  );
}
function AttachmentCard({
  file,
  onPreview,
  onRemove,
  disabled,
}: {
  file: DevTicketAttachment;
  onPreview: () => void;
  onRemove?: () => void;
  disabled?: boolean;
}) {
  const [image, setImage] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!file.content_type.startsWith('image/')) return;
    let active = true;
    let url = '';
    fetchApiBlob(`${file.download_url}?preview=true`)
      .then((blob) => {
        if (active) {
          url = URL.createObjectURL(blob);
          setImage(url);
        }
      })
      .catch(() => {
        /* Full preview/download still exposes a retryable error. */
      });
    return () => {
      active = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [file.download_url, file.content_type]);
  const canPreview = file.content_type.startsWith('image/') || file.content_type === 'application/pdf';
  return (
    <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'hidden', minWidth: 0 }}>
      {canPreview && (
        <Box
          component="button"
          type="button"
          onClick={onPreview}
          aria-label={`Preview ${file.name}`}
          sx={{
            width: '100%',
            height: 112,
            border: 0,
            bgcolor: 'background.level1',
            cursor: 'pointer',
            display: 'grid',
            placeItems: 'center',
            color: 'text.secondary',
          }}
        >
          {image ? (
            <Box
              component="img"
              src={image}
              alt={file.name}
              sx={{ width: '100%', height: 112, objectFit: 'cover' }}
            />
          ) : (
            <FileIcon size={30} />
          )}
        </Box>
      )}
      <Box sx={{ p: 1.25 }}>
        <Typography level="title-sm" sx={{ overflowWrap: 'anywhere' }}>
          {file.name}
        </Typography>
        <Typography level="body-xs">
          {bytes(file.size)} · {file.uploaded_by_name}
        </Typography>
        {file.comment && (
          <Chip size="sm" variant="plain" sx={{ my: 0.5 }}>
            From a comment
          </Chip>
        )}
        <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
          <Button
            size="sm"
            variant="plain"
            color="neutral"
            startDecorator={<DownloadSimple size={16} />}
            loading={loading}
            onClick={async () => {
              setError(null);
              setLoading(true);
              try {
                await downloadApiBlob(file.download_url, file.name);
              } catch (problem) {
                setError(problem);
              } finally {
                setLoading(false);
              }
            }}
          >
            Download
          </Button>
          {onRemove && (
            <IconButton
              size="sm"
              color="neutral"
              aria-label={`Delete attachment ${file.name}`}
              disabled={disabled}
              onClick={onRemove}
            >
              <Trash size={16} />
            </IconButton>
          )}
        </Box>
        <TicketError error={error} />
      </Box>
    </Sheet>
  );
}
function Preview({ file, onClose }: { file: DevTicketAttachment; onClose: () => void }) {
  const [url, setUrl] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    let objectUrl = '';
    fetchApiBlob(file.download_url)
      .then((blob) => {
        if (active) {
          objectUrl = URL.createObjectURL(blob);
          setUrl(objectUrl);
        }
      })
      .catch((problem) => {
        if (active) setError(problem);
      });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file.download_url, attempt]);
  return (
    <Modal open onClose={onClose}>
      <ModalDialog
        aria-labelledby="ticket-file-preview"
        sx={{ width: 'min(1100px, calc(100vw - 24px))', height: '85dvh', maxWidth: 'none' }}
      >
        <ModalClose aria-label="Close attachment preview" />
        <DialogTitle id="ticket-file-preview" sx={{ pr: 4, overflowWrap: 'anywhere' }}>
          {file.name}
        </DialogTitle>
        <TicketError
          error={error}
          retry={() => {
            setError(null);
            setUrl('');
            setAttempt((value) => value + 1);
          }}
        />
        {!url && !error && <LinearProgress aria-label="Loading attachment preview" />}
        {url &&
          (file.content_type === 'application/pdf' ? (
            <Box
              component="iframe"
              title={file.name}
              src={url}
              sx={{ flex: 1, border: 0, width: '100%', minHeight: 0 }}
            />
          ) : (
            <Box
              component="img"
              alt={file.name}
              src={url}
              sx={{ width: '100%', flex: 1, minHeight: 0, objectFit: 'contain' }}
            />
          ))}
      </ModalDialog>
    </Modal>
  );
}
export function AttachmentGallery({
  files,
  onRemove,
  disabled,
}: {
  files: DevTicketAttachment[];
  onRemove?: (file: DevTicketAttachment) => void;
  disabled?: boolean;
}) {
  const [preview, setPreview] = useState<DevTicketAttachment | null>(null);
  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(3, minmax(0, 1fr))',
          },
          gap: 1.25,
          my: files.length ? 1.5 : 0,
        }}
      >
        {files.map((file) => (
          <AttachmentCard
            key={file.id}
            file={file}
            disabled={disabled}
            onPreview={() => setPreview(file)}
            onRemove={onRemove ? () => onRemove(file) : undefined}
          />
        ))}
      </Box>
      {preview && <Preview file={preview} onClose={() => setPreview(null)} />}
    </>
  );
}
